import { useLayoutEffect, useRef, useState } from 'react';
import { addDays, diffDays, weekStart, formatFullDate, weekdayShort, dayOfMonth } from '@jungles/shared';
import './nutrition-days.css';

// A moving, bounded DOM window, not a limit on the user's diary history.
const WINDOW = 364;
const SHIFT = 91;
type Mark = { date: string; hasFood: boolean };
export function NutritionDays({ today, selected, week, onSelect }: {
  today: string; selected: string; week: Mark[]; onSelect: (date: string) => void;
}) {
  const end = addDays(weekStart(today), 6);
  const rangeFor = (date: string) => {
    const desired = addDays(weekStart(date), -SHIFT * 2);
    return desired > addDays(end, 1 - WINDOW) ? addDays(end, 1 - WINDOW) : desired;
  };
  const [start, setStart] = useState(() => rangeFor(selected));
  const strip = useRef<HTMLElement>(null);
  const previousStart = useRef(start);
  const previousSelected = useRef<string | null>(null);
  const focused = useRef<string | null>(null);
  const gesture = useRef<{ x: number; y: number } | null>(null);
  const suppressClick = useRef(false);
  const ready = useRef(false);
  const marks = useRef(new Map<string, boolean>());
  // API week dots are authoritative; browsing itself does not fetch meals.
  for (const item of week) marks.current.set(item.date, item.hasFood);
  for (const date of marks.current.keys()) {
    if (date < start || date > addDays(start, WINDOW - 1)) marks.current.delete(date);
  }
  const count = Math.min(WINDOW, diffDays(start, end) + 1);
  const dates = Array.from({ length: Math.max(0, count) }, (_, i) => addDays(start, i));
  useLayoutEffect(() => {
    const el = strip.current;
    if (!el) return;
    const stride = (el.firstElementChild?.getBoundingClientRect().width ?? (el.clientWidth - 30) / 7) + 5;
    // Preserve the exact visible date/pixel anchor when recycling either edge.
    if (previousStart.current !== start) {
      el.scrollLeft += diffDays(start, previousStart.current) * stride;
      previousStart.current = start;
    }
    if (previousSelected.current !== selected) {
      if (selected < start || selected > dates[dates.length - 1]!) {
        setStart(rangeFor(selected));
        return;
      }
      const offset = diffDays(start, selected) * stride;
      if (!ready.current || offset < el.scrollLeft || offset + stride - 5 > el.scrollLeft + el.clientWidth) {
        el.scrollLeft = diffDays(start, weekStart(selected)) * stride;
      }
      previousSelected.current = selected;
      ready.current = true;
    }
    if (focused.current) {
      const target = el.querySelector<HTMLButtonElement>(`[data-date="${focused.current}"]`);
      if (target) { target.focus({ preventScroll: true }); target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' }); focused.current = null; }
    }
  });
  const rewindow = () => {
    const el = strip.current;
    if (!el || !ready.current) return;
    const stride = (el.firstElementChild?.getBoundingClientRect().width ?? (el.clientWidth - 30) / 7) + 5;
    if (el.scrollLeft < stride * 21) setStart(addDays(start, -SHIFT));
    else if (el.scrollWidth - el.clientWidth - el.scrollLeft < stride * 21 && dates[dates.length - 1]! < end) {
      const next = addDays(start, SHIFT), latest = addDays(end, 1 - WINDOW);
      setStart(next > latest ? latest : next);
    }
  };
  return <nav ref={strip} className="wkn nutrition-days" aria-label="День недели" tabIndex={0}
    aria-description="Прокрутите дни и выберите дату. Стрелки клавиатуры перемещают фокус, Enter выбирает день."
    onScroll={rewindow}
    onKeyDown={event => {
      if (event.altKey || event.ctrlKey || event.metaKey || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      const el = event.currentTarget;
      const stride = (el.firstElementChild?.getBoundingClientRect().width ?? (el.clientWidth - 30) / 7) + 5;
      const visibleDate = dates[Math.min(dates.length - 1, Math.round(el.scrollLeft / stride))] ?? selected;
      const date = (event.target as HTMLElement).dataset.date ?? visibleDate;
      const next = addDays(date, event.key === 'ArrowLeft' ? -1 : 1);
      if (next > today) return;
      const target = strip.current?.querySelector<HTMLButtonElement>(`[data-date="${next}"]`);
      if (target) { target.focus({ preventScroll: true }); target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' }); }
      else { focused.current = next; setStart(rangeFor(next)); }
    }}
    onTouchStart={event => {
      const t = event.touches[0];
      if (event.touches.length !== 1) { gesture.current = null; suppressClick.current = true; return; }
      suppressClick.current = false;
      gesture.current = t ? { x: t.clientX, y: t.clientY } : null;
    }}
    onTouchMove={event => {
      const t = event.touches[0], origin = gesture.current;
      if (event.touches.length !== 1 || (t && origin && Math.hypot(t.clientX - origin.x, t.clientY - origin.y) > 8)) suppressClick.current = true;
    }}
    onTouchEnd={() => { gesture.current = null; }}
    onTouchCancel={() => { gesture.current = null; suppressClick.current = true; }}
    onClickCapture={event => {
      if (event.detail !== 0 && suppressClick.current) { event.preventDefault(); event.stopPropagation(); }
    }}>
    {dates.map(date => <button key={date} data-date={date} type="button"
      className={`dayn${date === selected ? ' on' : ''}${date > today ? ' fut' : ''}`}
      disabled={date > today} tabIndex={date === selected ? 0 : -1}
      aria-current={date === selected ? 'date' : undefined}
      aria-label={`${formatFullDate(date)}${date === today ? ', сегодня' : ''}`}
      onClick={() => onSelect(date)}>
      {weekdayShort(date)}<b>{dayOfMonth(date)}</b><i className={marks.current.get(date) ? 'food' : undefined} />
    </button>)}
  </nav>;
}
